import {
  formatCents,
  payoutRun,
  RECENTLY_PAID_DAYS,
  payoutTotal,
  reviewCutoff,
  toCents,
  toDecimal,
  type PayoutOrder,
  crewLabel,
} from "@roof-hawk/core";
import type React from "react";
import { useState } from "react";

import { ErrorNote, ListHead, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDate, fmtDateTime } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchPayoutQueue, updateRow } from "../../lib/queries";
import { useSession } from "../../session";

type Row = PayoutOrder & {
  id: string;
  order_number: number;
  job_id: string;
  trades: string[] | string | null;
  payout_note: string | null;
  /** QuickBooks already holds this payout's bill (ADR 0179). */
  bill_sent?: boolean;
  /** Who put it on hold, for the On hold row (ADR 0315). */
  holder?: { full_name: string | null } | null;
  job?: {
    job_number: number;
    is_test?: boolean | null;
    contact?: { full_name: string | null } | null;
  } | null;
};

/**
 * Crew payouts waiting on approval.
 *
 * Work orders completed by midnight Wednesday are the Thursday run. Approval is
 * the gate to QuickBooks — nothing becomes a bill until a person has said yes,
 * and what they said yes to is recorded against the work order.
 *
 * Work finished after the cutoff is shown separately rather than hidden: the
 * split is advice, and whoever is running the payout can pull one in.
 *
 * Our own employees never appear. They are on payroll, not a bill. Neither do
 * practice jobs: nobody is owed anything for a roof that was never installed,
 * and this screen is money leaving the company.
 *
 * `onChanged` is told after every write, so a screen beside the board that
 * reads the same work orders (the insurance owed under it, ADR 0275) can load
 * them again rather than show what was true before the press.
 */
export function PayoutBoard({ onChanged }: { onChanged?: () => void } = {}) {
  const { profileId } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [alreadyPaid, setAlreadyPaid] = useState<Row | null>(null);
  const [holding, setHolding] = useState<Row | null>(null);

  const queue = useAsync(fetchPayoutQueue, []);
  const rows = ((queue.data ?? []) as Row[]).filter(
    (row) => row.job?.is_test !== true,
  );
  const run = payoutRun(rows);
  const cutoff = reviewCutoff();

  async function patch(row: Row, values: Record<string, unknown>) {
    try {
      await updateRow("work_orders", row.id, values);
      setError(null);
      await queue.reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const approve = (row: Row, amount?: number | null) =>
    patch(row, {
      payout_approved_at: new Date().toISOString(),
      payout_approved_by: profileId,
      ...(amount === undefined ? {} : { payout_amount: amount }),
    });

  /**
   * Take the approval back.
   *
   * Once QuickBooks has the bill — seconds after approval, since ADR 0173 —
   * this asks first, and says what happens to the bill (ADR 0179): approving
   * again changes it to the new amount; not approving again leaves it in
   * QuickBooks for somebody to void there.
   */
  const unapprove = (row: Row) => {
    if (
      row.bill_sent &&
      !window.confirm(
        t("QuickBooks already has the bill for WO-{number} ({amount}).", {
          number: row.order_number,
          amount: formatCents(payoutTotal(row)),
        }) +
          "\n\n" +
          t(
            "Undo takes the approval back. Approve it again and QuickBooks gets the new amount on the same bill. If it is not approved again, the bill stays in QuickBooks until somebody voids it there.",
          ),
      )
    )
      return Promise.resolve();
    return patch(row, {
      payout_approved_at: null,
      payout_approved_by: null,
      payout_amount: null,
      payout_paid_manually_at: null,
      payout_paid_manually_by: null,
    });
  };

  /**
   * The money already went out of Mercury, by hand, before anybody reviewed
   * this. It is an approval — the cost is real and QuickBooks must carry it —
   * plus the payment itself, and a note that the process was not followed.
   */
  const markPaidAlready = (row: Row, amount: number | null) => {
    const now = new Date().toISOString();
    return patch(row, {
      payout_approved_at: now,
      payout_approved_by: profileId,
      payout_paid_at: now,
      payout_paid_by: profileId,
      payout_paid_manually_at: now,
      payout_paid_manually_by: profileId,
      payout_amount: amount,
    });
  };

  /** The ACH went out. Said by a person: Mercury cannot tell us. */
  const markPaid = (row: Row) =>
    patch(row, {
      payout_paid_at: new Date().toISOString(),
      payout_paid_by: profileId,
    });

  /** Thursday's run, ticked off in one press once the batch has gone. */
  async function markRunPaid() {
    const run_ = run.approved as Row[];
    if (run_.length === 0) return;
    if (
      !window.confirm(
        t(
          "Mark all {n} approved payouts as paid, totalling {amount}? Do this once the money has actually gone out of Mercury.",
          { n: run_.length, amount: formatCents(run.approvedTotal) },
        ),
      )
    )
      return;
    const now = new Date().toISOString();
    try {
      await Promise.all(
        run_.map((row) =>
          updateRow("work_orders", row.id, {
            payout_paid_at: now,
            payout_paid_by: profileId,
          }),
        ),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    await queue.reload();
    // Some of the run may have been written even when one write failed.
    onChanged?.();
  }

  /**
   * Paid in error, or not gone out after all. The approval stands, and the row
   * goes back to the Thursday run. A payment made before review loses its
   * "paid before review" note with it: the note is about a payment that is no
   * longer being said to have happened (ADR 0180). The database does the same
   * for any writer; saying it here keeps the write honest about what it does.
   */
  const unpay = (row: Row) =>
    patch(row, {
      payout_paid_at: null,
      payout_paid_by: null,
      payout_paid_manually_at: null,
      payout_paid_manually_by: null,
    });

  const reject = (row: Row, note: string) =>
    patch(row, {
      payout_rejected_at: new Date().toISOString(),
      payout_rejected_by: profileId,
      payout_note: note || null,
    });

  /**
   * Parked, with a reason: no W-9, no bank account, a placeholder order. Not
   * a rejection — the crew did nothing wrong — and not an approval, since
   * there is nowhere to send the money yet. The reason shares the note column
   * with Reject, because it answers the same question: why is this not paid?
   * (ADR 0315)
   */
  const hold = (row: Row, note: string) =>
    patch(row, {
      payout_held_at: new Date().toISOString(),
      payout_held_by: profileId,
      payout_note: note || null,
    });

  /**
   * The hold is over and nothing was decided: back to Needs review. The reason
   * goes with it, so the row does not carry a "no W-9" that is no longer true.
   */
  const release = (row: Row) =>
    patch(row, {
      payout_held_at: null,
      payout_held_by: null,
      payout_note: null,
    });

  const section = (
    title: string,
    hint: string,
    list: PayoutOrder[],
    kind: "review" | "approved" | "paid" | "rejected" | "held",
    action: React.ReactNode = null,
  ) => {
    const items = list as Row[];
    return (
      <div className="block" style={{ marginBottom: 16 }}>
        <h2>
          {title} <span className="na-hint">({items.length})</span>
          {action}
        </h2>
        <div className="na-hint" style={{ marginBottom: 10 }}>
          {hint}
        </div>
        {items.length === 0 ? (
          <div className="tnone">{t("Nothing here.")}</div>
        ) : (
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>{t("Work order")}</th>
                  <th>{t("Crew")}</th>
                  <th>{t("Job")}</th>
                  <th>{t("Completed")}</th>
                  <th className="num">{t("Amount")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="an">WO-{row.order_number}</div>
                      <div className="na-hint">
                        {Array.isArray(row.trades)
                          ? row.trades.join(", ")
                          : (row.trades ?? "—")}
                      </div>
                    </td>
                    <td className="sub">{crewLabel(row.crew) ?? "—"}</td>
                    <td>
                      <span
                        className="clink"
                        onClick={() => navigate(`job=${row.job_id}`)}
                      >
                        #{row.job?.job_number ?? "—"}
                      </span>
                      <div className="na-hint">
                        {row.job?.contact?.full_name ?? ""}
                      </div>
                    </td>
                    <td className="sub">
                      {row.completed_at ? fmtDate(row.completed_at) : "—"}
                    </td>
                    <td className="num">
                      {formatCents(payoutTotal(row))}
                      {row.payout_amount != null && (
                        <div className="na-hint">{t("partial")}</div>
                      )}
                    </td>
                    <td className="rowacts">
                      {kind === "review" && (
                        <>
                          <button
                            className="btn-small"
                            onClick={() => void approve(row)}
                          >
                            {t("Approve")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => setEditing(row)}
                          >
                            {t("Change amount")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => setRejecting(row)}
                          >
                            {t("Reject")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => setAlreadyPaid(row)}
                          >
                            {t("Already paid")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => setHolding(row)}
                          >
                            {t("Hold")}
                          </button>
                        </>
                      )}
                      {kind === "approved" && (
                        <>
                          <button
                            className="btn-small"
                            onClick={() => void markPaid(row)}
                          >
                            {t("Mark paid")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => void unapprove(row)}
                          >
                            {t("Undo")}
                          </button>
                        </>
                      )}
                      {kind === "paid" && (
                        <>
                          {row.payout_paid_manually_at && (
                            <div className="na-hint">
                              {t("paid before review")}
                            </div>
                          )}
                          <button
                            className="btn-small"
                            onClick={() => void unpay(row)}
                          >
                            {t("Not paid")}
                          </button>
                        </>
                      )}
                      {kind === "rejected" && (
                        <>
                          <div className="na-hint">{row.payout_note}</div>
                          <button
                            className="btn-small"
                            onClick={() => void approve(row)}
                          >
                            {t("Approve now")}
                          </button>
                        </>
                      )}
                      {kind === "held" && (
                        <>
                          <div className="na-hint">{row.payout_note}</div>
                          <div className="na-hint">{heldBy(row)}</div>
                          <button
                            className="btn-small"
                            onClick={() => void approve(row)}
                          >
                            {t("Approve now")}
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => void release(row)}
                          >
                            {t("Back to review")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ListHead
        title={t("Crew Payouts")}
        count={run.due.length + run.early.length + run.approved.length}
      >
        <span className="paytotal">
          {t("{amount} approved", { amount: formatCents(run.approvedTotal) })}
        </span>
      </ListHead>

      <ErrorNote error={error} />

      <div className="na-hint" style={{ margin: "0 0 14px" }}>
        {t(
          "This run covers work completed before midnight on {date}. Our own crews are not here — they are on payroll, not a bill.",
          {
            date: fmtDate(
              new Date(cutoff.getTime() - 86_400_000).toISOString(),
            ),
          },
        )}
      </div>

      {section(
        t("Needs review"),
        t(
          "Completed before the cutoff. Approve, change the amount, or send it back.",
        ),
        run.due,
        "review",
      )}
      {section(
        t("Finished after the cutoff"),
        t(
          "Not due until next Thursday — approve one anyway if it should go out today.",
        ),
        run.early,
        "review",
      )}
      {section(
        t("Approved, to pay"),
        t(
          "These are Thursday's ACH. QuickBooks already has the cost — mark each one paid once the money has gone, or use the button above. Marking one paid also ticks Crew paid on its work order.",
        ),
        run.approved,
        "approved",
        run.approved.length > 0 ? (
          <button className="btn-small" onClick={() => void markRunPaid()}>
            {t("Mark all paid")}
          </button>
        ) : null,
      )}
      {section(
        t("Paid"),
        t(
          "The money has gone. These drop off the board after {days} days; the work order still shows the payout.",
          { days: RECENTLY_PAID_DAYS },
        ),
        run.paid,
        "paid",
      )}
      {section(
        t("On hold"),
        t(
          "Still owed, but waiting on something — a W-9, bank details, or a decision. Approve now once it is sorted, or put it back in review.",
        ),
        run.held,
        "held",
      )}
      {section(
        t("Sent back"),
        t("Rejected with a note. These come back once the crew has sorted it."),
        run.rejected,
        "rejected",
      )}

      {editing && (
        <AmountModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={async (amount) => {
            await approve(editing, amount);
            setEditing(null);
          }}
        />
      )}

      {alreadyPaid && (
        <AlreadyPaidModal
          row={alreadyPaid}
          onClose={() => setAlreadyPaid(null)}
          onSave={async (amount) => {
            await markPaidAlready(alreadyPaid, amount);
            setAlreadyPaid(null);
          }}
        />
      )}

      {rejecting && (
        <RejectModal
          row={rejecting}
          onClose={() => setRejecting(null)}
          onSave={async (note) => {
            await reject(rejecting, note);
            setRejecting(null);
          }}
        />
      )}

      {holding && (
        <HoldModal
          row={holding}
          onClose={() => setHolding(null)}
          onSave={async (note) => {
            await hold(holding, note);
            setHolding(null);
          }}
        />
      )}
    </>
  );
}

/** A partial approval: the crew did most of it, so pay for what they did. */
function AmountModal({
  row,
  onClose,
  onSave,
}: {
  row: Row;
  onClose: () => void;
  onSave: (amount: number) => Promise<void>;
}) {
  const [value, setValue] = useState(
    toDecimal(toCents(row.payout_amount ?? 0) || payoutTotal(row)),
  );

  return (
    <Modal
      title={t("Approve WO-{number} for a different amount", {
        number: row.order_number,
      })}
      subtitle={t(
        "Use this when the crew did not finish everything. The amount here replaces the order total for payment; the work order itself is unchanged.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            onClick={() => void onSave(Number(value))}
          >
            {t("Approve this amount")}
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="payout-amount">{t("Amount to pay")}</label>
        <input
          id="payout-amount"
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
        />
        <div className="na-hint">
          {t("Order total is {amount}.", {
            amount: formatCents(payoutTotal({ ...row, payout_amount: null })),
          })}
        </div>
      </div>
    </Modal>
  );
}

/**
 * A payout that was already paid, by hand, out of Mercury.
 *
 * The amount is asked for because what left the bank is the truth: it is
 * usually the order total, and when it is not, the bill QuickBooks gets must
 * be what was actually paid rather than what was booked.
 */
function AlreadyPaidModal({
  row,
  onClose,
  onSave,
}: {
  row: Row;
  onClose: () => void;
  onSave: (amount: number | null) => Promise<void>;
}) {
  const total = payoutTotal({ ...row, payout_amount: null });
  const [value, setValue] = useState(toDecimal(total));

  return (
    <Modal
      title={t("WO-{number} has already been paid", {
        number: row.order_number,
      })}
      subtitle={t(
        "Use this when the crew was paid by hand, straight out of Mercury. It leaves the Thursday run — nothing is sent for it — and the cost still goes to QuickBooks, where the Mercury payment matches against it.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            onClick={() =>
              void onSave(toCents(value) === total ? null : Number(value))
            }
          >
            {t("Mark as paid")}
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="paid-amount">{t("Amount that was paid")}</label>
        <input
          id="paid-amount"
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
        />
        <div className="na-hint">
          {t("Order total is {amount}.", { amount: formatCents(total) })}
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({
  row,
  onClose,
  onSave,
}: {
  row: Row;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");

  return (
    <Modal
      title={t("Send WO-{number} back", { number: row.order_number })}
      subtitle={t(
        "It leaves this run and waits. Say why, so the crew knows what to fix.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={!note.trim()}
            onClick={() => void onSave(note.trim())}
          >
            {t("Send back")}
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="reject-note">{t("Reason")}</label>
        <input
          id="reject-note"
          value={note}
          placeholder={t("What needs sorting before this is paid")}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {row.completed_at && (
        <div className="na-hint">
          {t("Marked complete {when}.", {
            when: fmtDateTime(row.completed_at),
          })}
        </div>
      )}
    </Modal>
  );
}

/** Who parked it and when, the way the work order's payout line says it. */
function heldBy(row: Row): string {
  const who = row.holder?.full_name;
  const when = row.payout_held_at ? fmtDateTime(row.payout_held_at) : null;
  const said = [who ? t("by {who}", { who }) : null, when]
    .filter(Boolean)
    .join(", ");
  return said ? t("On hold {said}", { said }) : t("On hold");
}

/**
 * Put a payout on hold. The reason is required for the same reason Reject's
 * is: a parked payout with no note is a payout nobody knows what to do with,
 * which is the problem Hold exists to fix (ADR 0315).
 */
function HoldModal({
  row,
  onClose,
  onSave,
}: {
  row: Row;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");

  return (
    <Modal
      title={t("Put WO-{number} on hold", { number: row.order_number })}
      subtitle={t(
        "It is still owed, but it cannot be paid yet. It leaves this list and waits under On hold with the reason, until you approve it or put it back.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={!note.trim()}
            onClick={() => void onSave(note.trim())}
          >
            {t("Put on hold")}
          </button>
        </>
      }
    >
      <div className="field full">
        <label htmlFor="hold-note">{t("Reason")}</label>
        <input
          id="hold-note"
          value={note}
          placeholder={t("Missing W-9, no bank details, placeholder order…")}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}
